/**
 * Phone number formatting utilities for iMask pattern masks.
 *
 * iMask format conventions:
 *   {X}  – fixed literal character X (e.g. {1} = country code "1")
 *   0    – required digit slot
 *   +, (, ), -, space – literal decorations
 *
 * Example format: "+{1} (000) 000-0000"
 *   Display:  +1 (333) 555-1212
 *   API:      (333) 555-1212      (national format, stored locally)
 *   E.164:    +13335551212        (sent to server)
 */

// US/Canada only — application is deployed exclusively in the US
const DEFAULT_TEL_FORMAT = '+{1} (000) 000-0000';

/**
 * Compute the display prefix produced by fixed literal groups at the start
 * of an iMask format string, including any trailing whitespace.
 *
 * "+{1} (000) 000-0000"  →  "+1 "   (3 display chars)
 * "(000) 000-0000"       →  ""      (no fixed groups)
 */
export function computeFormatPrefix(format: string): string {
  // Find the last closing brace that appears before the first definition char
  let lastBraceEnd = -1;

  for (let i = 0; i < format.length; i++) {
    if (format[i] === '{') {
      const close = format.indexOf('}', i);
      if (close !== -1) {
        lastBraceEnd = close;
        i = close;
      }
    } else if ('0a*'.includes(format[i])) {
      break;
    }
  }

  if (lastBraceEnd === -1) return '';

  // Resolve the display characters up to lastBraceEnd
  let prefix = '';
  for (let i = 0; i <= lastBraceEnd; i++) {
    if (format[i] === '{') {
      const close = format.indexOf('}', i);
      if (close !== -1) {
        prefix += format.substring(i + 1, close);
        i = close;
      }
    } else {
      prefix += format[i];
    }
  }

  // Include trailing whitespace
  let i = lastBraceEnd + 1;
  while (i < format.length && format[i] === ' ') {
    prefix += ' ';
    i++;
  }

  return prefix;
}

/**
 * Strip the country-code display prefix from a fully masked phone value,
 * returning the national format that matches the API storage format.
 *
 * stripFormatPrefix("+1 (333) 555-1212", "+{1} (000) 000-0000")
 *   → "(333) 555-1212"
 */
export function stripFormatPrefix(maskedValue: string, format: string): string {
  const prefix = computeFormatPrefix(format);
  if (prefix && maskedValue.startsWith(prefix)) {
    return maskedValue.slice(prefix.length);
  }
  return maskedValue;
}

/**
 * Extract the country code from {…} groups in an iMask format string.
 *
 * "+{1} (000) 000-0000"  →  "1"
 */
function extractCountryCode(format: string): string {
  let code = '';
  for (let i = 0; i < format.length; i++) {
    if (format[i] === '{') {
      const close = format.indexOf('}', i);
      if (close !== -1) {
        code += format.substring(i + 1, close);
        i = close;
      }
    } else if ('0a*'.includes(format[i])) {
      break;
    }
  }
  return code;
}

/**
 * Convert a formatted phone number to E.164 format.
 * Strips non-digit characters from the value, then prepends + and the
 * country code extracted from the iMask format.
 *
 * toE164("(333) 555-1212", "+{1} (000) 000-0000")  →  "+13335551212"
 * toE164("",               "+{1} (000) 000-0000")  →  ""
 */
export function toE164(value: string, format: string = DEFAULT_TEL_FORMAT): string {
  if (!value) return value;

  const digits = value.replace(/\D/g, '');
  if (!digits) return '';

  const countryCode = extractCountryCode(format);
  // Strip country code if digits already start with it (e.g. input "+1 (333) 555-1212")
  const normalizedDigits = digits.startsWith(countryCode) ? digits.slice(countryCode.length) : digits;
  return `+${countryCode}${normalizedDigits}`;
}

/**
 * Walk a template form config and collect field names that have `as: "tel"`,
 * mapped to their format string (or the default).
 */
export function findTelFields(
  formConfig: Record<string, unknown>
): Map<string, string> {
  const result = new Map<string, string>();

  for (const [key, raw] of Object.entries(formConfig)) {
    if (!raw || typeof raw !== 'object') continue;
    const fc = raw as Record<string, unknown>;

    if (fc.as === 'tel') {
      result.set(
        key,
        typeof fc.format === 'string' ? fc.format : DEFAULT_TEL_FORMAT
      );
    }

    // Recurse into FieldGroup fields
    if (fc.fields && typeof fc.fields === 'object') {
      for (const [k, v] of findTelFields(fc.fields as Record<string, unknown>)) {
        result.set(k, v);
      }
    }

    // Recurse into FieldTable rows
    if (fc.rows && typeof fc.rows === 'object') {
      for (const rowVal of Object.values(fc.rows as Record<string, unknown>)) {
        if (rowVal && typeof rowVal === 'object') {
          const row = rowVal as { fields?: Record<string, unknown> };
          if (row.fields) {
            for (const [k, v] of findTelFields(row.fields)) {
              result.set(k, v);
            }
          }
        }
      }
    }

    // Recurse into BasicList attributes
    if (fc.attributes && typeof fc.attributes === 'object') {
      for (const [k, v] of findTelFields(fc.attributes as Record<string, unknown>)) {
        result.set(k, v);
      }
    }
  }

  return result;
}

/**
 * Convert all tel field values in a super_form payload to E.164 format.
 * Returns a shallow copy with tel fields converted; non-tel fields untouched.
 */
export function convertTelFieldsToE164(
  superForm: Record<string, unknown>,
  telFields: Map<string, string>
): Record<string, unknown> {
  if (telFields.size === 0) return superForm;

  const result = { ...superForm };
  for (const [fieldName, format] of telFields) {
    if (fieldName in result && typeof result[fieldName] === 'string') {
      result[fieldName] = toE164(result[fieldName] as string, format);
    }
  }
  return result;
}
